import { fmtCalendarDate } from '../../shared/Panel';
import { dateTimeNoSeconds, shortDateTime } from '../../shared/dateTime';
import { plural } from '../../shared/plural';
import { isImportantAlert, type TmsAlert } from '../userOffers/orderActionsApi';
import { SOON_DAYS, daysUntil, sortByTerm, type VehicleEligibilityRow } from './eligibilityTerms';
import { currentAndNext } from './ganttRules';
import { pastWindow, planPhase } from './planRows';
import { routeText } from './timeline';
import { isOrderVehicle } from './vehicleTypes';
import type { PlanItem } from './vehiclePlanApi';

/**
 * Panel „Do zrobienia” na karcie pojazdu — czysta logika (15.10.2026, makieta
 * FTL p. Mariusza 13.10, PROMPT.md pkt 9.2 i 16; wzorzec
 * `wzorzec-pojazd-i-zlecenie.html`, `v-todo`; Etap 1 — decyzja Bartka
 * 15.10.2026, przełącznik `vehicleTodoPanel`).
 *
 * **Tylko z tego, co już mamy** — kartoteka z TMS (eksploatacja, dostępność,
 * kierowca, terminy i status uprawnień) i plan auta (alerty obsadzeń,
 * warianty, następne zlecenie). Makieta ma tu też awarię i wyłączenie
 * z planowania wpisywane przez planistę i luki z liczbą pasujących zleceń
 * — tego nie budujemy: wpisy ręczne obok TMS czekają na decyzje Mariusza,
 * a liczba pasujących zleceń wymaga przeliczenia rekomendacji przy każdym
 * otwarciu karty. Korekty kartoteki — od 22.10.2026 (`fleetCorrections`,
 * decyzja użytkownika 22.10.2026): pozycja „Kartoteka różni się od TMS”,
 * jak w makiecie.
 *
 * Panel niczego nie zmienia i nie ocenia — mówi, co planista powinien
 * sprawdzić, i prowadzi tam, gdzie to się robi (karta zlecenia, rekomendacje).
 * Brak danych to brak pozycji, nie „wszystko w porządku”: gdy plan się nie
 * wczytał, panel mówi to wprost.
 */

/** Waga pozycji — kolor paska i kolejność: najpierw blokujące, na końcu informacje. */
export type TodoTone = 'error' | 'warning' | 'info' | 'ok';

export interface TodoOrderRef {
  id: number;
  number: string;
}

export interface TodoItem {
  key: string;
  tone: TodoTone;
  title: string;
  detail: string;
  /** Zlecenia, do których prowadzi pozycja (odsyłacze do kart). */
  orders?: TodoOrderRef[];
  /** „Pokaż rekomendowane zlecenia” — przejście do sekcji rekomendacji. */
  showSuggestions?: boolean;
}

/** Pola kartoteki auta, które czyta panel. */
export interface TodoCar {
  carType?: unknown;
  operational?: unknown;
  available?: unknown;
  unavailableStatus?: unknown;
  unavailableTo?: unknown;
  driver?: unknown;
  eligibilities?: unknown;
  /** Aktywne korekty kartoteki — tylko z przełącznikiem `fleetCorrections`. */
  corrections?: unknown;
  /** Reszta rekordu `GET /cars/:id` — panel jej nie czyta. */
  [key: string]: unknown;
}

/** Plan auta: pozycje albo — gdy nie wiadomo — stan wczytywania. */
export type TodoPlan = { items: readonly PlanItem[] } | { loading: true } | { error: string };

const TONE_ORDER: Record<TodoTone, number> = { error: 0, warning: 1, info: 2, ok: 3 };

const text = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v.trim() : null);

const numberOf = (i: Pick<PlanItem, 'number' | 'orderId'>) => i.number ?? `#${i.orderId}`;

const refOf = (i: Pick<PlanItem, 'number' | 'orderId'>): TodoOrderRef => ({ id: i.orderId, number: numberOf(i) });

/** Tytuły alertów — jak w pasku podsumowania i banerze (`CarSummary.ALERT_TITLE`). */
const ALERT_TITLES: Record<TmsAlert['kind'], string> = {
  replan: 'Rekomendowane przeplanowanie',
  change: 'Zmiana w TMS po obsadzeniu',
  cancellation: 'Anulacja w TMS — do potwierdzenia',
  vehicleChange: 'Zmiana pojazdu w TMS po obsadzeniu',
};

/** „za 3 dni”, „jutro”, „dziś”, „wygasło 2 dni temu”. */
export function termText(days: number): string {
  if (days < 0) return days === -1 ? 'wygasło wczoraj' : `wygasło ${plural(-days, 'dzień', 'dni', 'dni')} temu`;
  if (days === 0) return 'dziś';
  if (days === 1) return 'jutro';
  return `za ${plural(days, 'dzień', 'dni', 'dni')}`;
}

/** Pozycje kartoteki — niezależne od planu. */
function registryItems(car: TodoCar, now: Date): TodoItem[] {
  const out: TodoItem[] = [];
  if (car.operational === false) {
    out.push({
      key: 'operational',
      tone: 'error',
      title: 'Pojazd poza eksploatacją w TMS',
      detail: 'Rekomendacje go pomijają. Zmiana tylko w TMS.',
    });
  }
  if (car.available === false) {
    const until = text(car.unavailableTo);
    out.push({
      key: 'available',
      tone: 'error',
      title: 'Pojazd niedostępny w TMS',
      detail: `${text(car.unavailableStatus) ?? 'TMS nie podaje powodu'}${
        until ? `, do ${dateTimeNoSeconds(until)}` : ''
      }.`,
    });
  }
  // Zleceń serwisowych z TMS nie pokazujemy (odpowiedź Marathonu 16.10.2026,
  // pyt. 14: zasób dla innego projektu) — dostępność mówią pola pojazdu wyżej.
  const corrections = Array.isArray(car.corrections)
    ? (car.corrections as Array<{ field?: unknown; warning?: unknown }>)
    : [];
  const driverRemoved = corrections.some((c) => c.field === 'driver');
  if (isOrderVehicle(car.carType) && (car.driver === null || car.driver === undefined)) {
    out.push(
      driverRemoved
        ? {
            key: 'driver',
            tone: 'warning',
            title: 'Pojazd bez kierowcy — korekta kartoteki',
            detail: 'Rekomendacje go pomijają, dopóki korekta obowiązuje. Kierowcę z TMS przywraca się w sekcji „Kierowca i zestaw”.',
          }
        : {
            key: 'driver',
            tone: 'warning',
            title: 'Pojazd bez kierowcy w kartotece TMS',
            detail: 'Kierowcę przypisuje się w TMS. Bez niego nie znamy czasu pracy kierowcy ani jego nieobecności.',
          },
    );
  }
  if (corrections.length > 0) {
    const fields = corrections
      .map((c) => (c.field === 'driver' ? 'kierowca' : c.field === 'trailer' ? 'naczepa' : 'terminy uprawnień'))
      .filter((f, i, all) => all.indexOf(f) === i);
    const changed = corrections.filter((c) => typeof c.warning === 'string' && c.warning !== '').length;
    out.push({
      key: 'corrections',
      tone: changed > 0 ? 'warning' : 'info',
      title: 'Kartoteka różni się od TMS',
      detail: `Są wartości skorygowane w aplikacji: ${fields.join(', ')}.${
        changed > 0 ? ` TMS zmienił się od korekty: ${changed} — sprawdź znaczniki „≠ TMS”.` : ''
      }`,
    });
  }
  const eligibilities = Array.isArray(car.eligibilities) ? (car.eligibilities as VehicleEligibilityRow[]) : [];
  for (const e of sortByTerm(eligibilities)) {
    // Status z TMS `false` = nieważne (odpowiedź Marathonu 16.10.2026, pyt. 8)
    // — bez względu na termin; Blokada blokująco, jak w rekomendacjach.
    if (e.status === false) {
      out.push({
        key: `invalid-${e.name}-${e.validUpTo}`,
        tone: e.type === 'Blokada' ? 'error' : 'warning',
        title: `Nieważne według TMS: ${e.name.toLowerCase()}`,
        detail: `${e.validUpTo ? `Ważne do ${fmtCalendarDate(e.validUpTo)}` : 'Bez terminu'}${
          e.type ? ` (${e.type})` : ''
        }. Zmiana tylko w TMS.`,
      });
      continue;
    }
    const days = daysUntil(e.validUpTo, now);
    if (days === null || days > SOON_DAYS) continue;
    out.push({
      key: `term-${e.name}-${e.validUpTo}`,
      tone: days < 0 && e.type === 'Blokada' ? 'error' : 'warning',
      title: `Termin z kartoteki TMS: ${e.name.toLowerCase()}`,
      detail: `${fmtCalendarDate(e.validUpTo)} · ${termText(days)}${e.type ? ` (${e.type})` : ''}.`,
    });
  }
  return out;
}

/** Pozycje z planu auta: alerty, brak następnego zlecenia, warianty, zlecenia po oknie. */
function planItems(car: TodoCar, items: readonly PlanItem[], now: Date): TodoItem[] {
  const out: TodoItem[] = [];
  const active = items.filter((i) => planPhase(i) !== 'done');
  for (const i of active) {
    const alerts = i.kind === 'assignment' ? (i.alerts ?? []) : [];
    if (alerts.length === 0) continue;
    out.push({
      key: `alert-${i.orderId}`,
      tone: alerts.some((a) => isImportantAlert(a)) ? 'error' : 'warning',
      title: `${ALERT_TITLES[alerts[0].kind]} · ${numberOf(i)}`,
      detail: `${alerts.length > 1 ? `Alerty: ${alerts.length}. ` : ''}Potwierdza się na karcie zlecenia.`,
      orders: [refOf(i)],
    });
  }
  const { current, next } = currentAndNext(items, now);
  if (!current && !next && isOrderVehicle(car.carType) && car.operational !== false) {
    const last = items
      .filter((i) => i.kind === 'assignment' && i.occupancyTo && new Date(i.occupancyTo).getTime() <= now.getTime())
      .sort((a, b) => new Date(b.occupancyTo as string).getTime() - new Date(a.occupancyTo as string).getTime())[0];
    out.push({
      key: 'idle',
      tone: 'warning',
      title: 'Pojazd bez zaplanowanego zlecenia',
      detail: last
        ? `Ostatnie: ${numberOf(last)} (${routeText(last)}), szacowany koniec ${shortDateTime(last.occupancyTo as string)}.`
        : 'W planie od tygodnia wstecz nie ma żadnego obsadzenia.',
      orders: last ? [refOf(last)] : undefined,
      showSuggestions: true,
    });
  }
  const variants = active.filter((i) => i.kind === 'candidate');
  const waiting = variants.filter((i) => !i.assignedElsewhere);
  if (waiting.length > 0) {
    out.push({
      key: 'variants',
      tone: 'warning',
      title: `Warianty czekające na decyzję: ${waiting.length}`,
      detail: 'Obsadzenie tym pojazdem albo usunięcie wariantu — na karcie zlecenia.',
      orders: waiting.map(refOf),
    });
  }
  const elsewhere = variants.filter((i) => i.assignedElsewhere);
  if (elsewhere.length > 0) {
    out.push({
      key: 'variants-elsewhere',
      tone: 'info',
      title: `Warianty zleceń obsadzonych innym pojazdem: ${elsewhere.length}`,
      detail: 'Zlecenie jedzie innym autem — wariant można zwolnić.',
      orders: elsewhere.map(refOf),
    });
  }
  const past = items.filter((i) => i.kind === 'assignment' && pastWindow(i, now));
  if (past.length > 0) {
    out.push({
      key: 'past-window',
      tone: 'warning',
      title: `Zlecenia po oknie, niezamknięte w TMS: ${past.length}`,
      detail: 'Okno minęło, a TMS nie oznaczył zlecenia jako zrealizowane — sprawdź status w TMS.',
      orders: past.map(refOf),
    });
  }
  return out;
}

/**
 * Pozycje panelu od najważniejszych. Bez żadnej — jedna pozycja „Nic do
 * zrobienia”; plan nieznany (wczytuje się, błąd) — pozycja, która to mówi.
 */
export function carTodoItems(car: TodoCar, plan: TodoPlan, now: Date): TodoItem[] {
  const out = registryItems(car, now);
  if ('items' in plan) out.push(...planItems(car, plan.items, now));
  else if ('error' in plan) {
    out.push({
      key: 'plan-error',
      tone: 'info',
      title: 'Nie udało się wczytać planu pojazdu',
      detail: `Alertów, wariantów i następnego zlecenia tu nie widać: ${plan.error}`,
    });
  } else {
    out.push({ key: 'plan-loading', tone: 'info', title: 'Wczytuję plan pojazdu…', detail: 'Alerty i warianty pojawią się za chwilę.' });
  }
  if (out.length === 0) {
    return [{ key: 'none', tone: 'ok', title: 'Nic do zrobienia', detail: 'Pojazd nie wymaga teraz działania planisty.' }];
  }
  return out
    .map((item, index) => ({ item, index }))
    .sort((a, b) => TONE_ORDER[a.item.tone] - TONE_ORDER[b.item.tone] || a.index - b.index)
    .map((x) => x.item);
}
